import React, { useMemo } from 'react';
import { Stack } from '@fiverr-private/layout_components';
import { CURRENCY_STYLES, currencyFormat } from '@fiverr-private/localization';
import { Heading, Text } from '@fiverr-private/typography';
import { PackageDetails } from '../types';
import { useResellingProgramsContext } from '../../../context';
import { BillingCycle, PricingDetails } from '../../../../type';
import { appTranslate } from '../../../i18n';

interface RegularPricingProps {
    tierName: PackageDetails['id'];
    pricingDetails?: PricingDetails;
}

export const RegularPricing = ({ tierName, pricingDetails }: RegularPricingProps) => {
    const { getSubscriptionDetailsByTier } = useResellingProgramsContext();

    const monthlyPrice = useMemo(() => {
        const tierDetails = getSubscriptionDetailsByTier(tierName).get(BillingCycle.MONTHLY);

        return tierDetails?.price;
    }, [tierName, getSubscriptionDetailsByTier]);

    if (!pricingDetails?.price) {
        return null;
    }

    return (
        <Stack rowGap="1" direction="column">
            <Stack columnGap="2" alignItems="center">
                <Heading as="h1" size={{ default: 'h_xl', md: 'h_xxl', lg: 'h_xl', xl: 'h_xxl' }} whiteSpace="nowrap">
                    {currencyFormat({
                        value: pricingDetails.price.amountMonthly,
                        currencyCode: pricingDetails.price.currency,
                        options: {
                            maximumFractionDigits: 2,
                            style: CURRENCY_STYLES.SYMBOL,
                        },
                    })}
                </Heading>

                {pricingDetails.billingCycle !== BillingCycle.MONTHLY && monthlyPrice && (
                    <Text
                        size={{ default: 'b_md', md: 'b_lg', lg: 'b_md', xl: 'b_lg' }}
                        decoration="lineThrough"
                        paddingBottom="0.5"
                    >
                        {currencyFormat({
                            value: monthlyPrice.amountMonthly,
                            currencyCode: monthlyPrice.currency,
                            options: {
                                maximumFractionDigits: 2,
                                style: CURRENCY_STYLES.SYMBOL,
                            },
                        })}
                    </Text>
                )}
            </Stack>

            {pricingDetails.billingCycle === BillingCycle.MONTHLY && (
                <Text size="b_sm" color="bodySecondary">
                    {appTranslate('compare_packages.microsoft.pricing.billing_cycle.monthly')}
                </Text>
            )}
            {pricingDetails.billingCycle === BillingCycle.YEARLY && (
                <Text size="b_sm" color="bodySecondary">
                    {appTranslate('compare_packages.microsoft.pricing.billing_cycle.yearly')}
                </Text>
            )}
        </Stack>
    );
};
